import { ENTITY_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
import { t } from '@/ui/i18n/i18n';
import { Ref } from 'vue';
import { PaginationResetCallback, SelectOptions } from '@/ui/types';
import { tagFilter } from './tagFilter';
import { dateRangeFilter as baseDateRangeFilter } from './dateFilter';
import { textFilter } from './textFilter';
import { checkboxFilter } from './checkboxFilter';
import { freeTagFilter } from './freeTagFilter';
import {
  inspectionStatusOptions,
  issuePriorityOptions,
  issueStatusOptions,
  issueTypeOptions,
} from '@/ui/components/filters/options';
import { templateCategory } from '@/ui/auth/templates';
import { dropdownFilter } from '@/ui/components/filters/dropdownFilter';

const dummyCallback = () => {
};

export const searchFilter = (
  value: Ref<string>,
  reset: PaginationResetCallback = dummyCallback,
  icon?: string | undefined,
  hideLabel?: boolean,
  placeholder = t('form.enter-search'),
) => textFilter({
  value,
  name: 'search',
  label: t('common.search'),
  placeholder,
  reset,
  icon,
  hideLabel,
});

export const dateRangeFilter = (
  start: Ref<Date>,
  end: Ref<Date>,
  reset: PaginationResetCallback = dummyCallback,
  rangeLimitMonths?: number,
) => baseDateRangeFilter({
  start,
  end,
  name: 'date',
  label: t('common.date'),
  placeholder: t('form.date-range'),
  clearable: false,
  reset,
  rangeLimitMonths,
});

export const lastUpdatedFilter = (
  start: Ref<Date | null>,
  end: Ref<Date | null>,
  reset: PaginationResetCallback = dummyCallback,
) => baseDateRangeFilter({
  start,
  end,
  name: 'lastUpdated',
  label: t('common.last-updated'),
  placeholder: t('form.date-range'),
  clearable: true,
  reset,
});

export const createdAtFilter = (
  start: Ref<Date | null>,
  end: Ref<Date | null>,
  reset: PaginationResetCallback = dummyCallback,
) => baseDateRangeFilter({
  start,
  end,
  name: 'createdAt',
  label: t('common.created-at'),
  placeholder: t('form.date-range'),
  clearable: true,
  reset,
});

export const entityItemFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'entityItem',
  label: t('common.entity-item'),
  placeholder: t('common.all'),
  options,
  reset,
  maxListedOptions: ENTITY_ITEMS_DROPDOWN_LIMIT,
});

export const entityFolderFilter = (
  value: Ref<string[]>,
  options: SelectOptions<string>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'entityFolder',
  label: t('common.entity-folder'),
  placeholder: t('common.all'),
  options,
  reset,
  maxListedOptions: ENTITY_ITEMS_DROPDOWN_LIMIT,
});

export const assigneeFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'assignee',
  label: t('common.assignee'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const creatorFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'creator',
  label: t('common.creator'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const resolverFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'resolver',
  label: t('common.resolver'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const issueTypeFilter = (
  value: Ref<string[]>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter<string>({
  value,
  name: 'type',
  label: t('common.type'),
  placeholder: t('common.all'),
  options: issueTypeOptions(),
  reset,
});

export const issuePriorityTagFilter = (
  value: Ref<number[]>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'type',
  label: t('common.priority'),
  placeholder: t('common.all'),
  options: issuePriorityOptions(),
  reset,
});

export const inspectorFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'inspector',
  label: t('common.inspector'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const userFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'user',
  label: t('common.user'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const sensorFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'sensor',
  label: t('common.sensor'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const questionTitleFilter = (
  value: Ref<string[]>,
  reset: PaginationResetCallback = dummyCallback,
) => freeTagFilter({
  value,
  name: 'questionTitle',
  label: t('inspections.questions'),
  placeholder: t('common.all'),
  reset,
});

export const responseFilter = (
  value: Ref<string[]>,
  reset: PaginationResetCallback = dummyCallback,
) => freeTagFilter({
  value,
  name: 'response',
  label: t('common.response'),
  placeholder: t('common.all'),
  reset,
});

export const sectionFilter = (
  value: Ref<string[]>,
  options: SelectOptions<string>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter<string>({
  value,
  name: 'section',
  label: t('inspections.section'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const groupFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'group',
  label: t('common.group'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const roleFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'role',
  label: t('common.role'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const categoryFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'category',
  label: t('common.category'),
  placeholder: t('common.categories'),
  options,
  reset,
});

export const checklistFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'checklist',
  label: t('common.checklist'),
  placeholder: t('common.all'),
  options,
  reset,
});

export const issueStatusFilter = (
  value: Ref<number[]>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'issueStatus',
  label: t('common.status'),
  placeholder: t('common.all'),
  options: issueStatusOptions(),
  reset,
});

export const inspectionStatusFilter = (
  value: Ref<number[]>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'status',
  label: t('common.status'),
  placeholder: t('common.all'),
  //Status.CantDo and Status.InProgress are not for Inspections
  options: inspectionStatusOptions(),
  reset,
});

export const todoPriorityFilter = (
  value: Ref<boolean>,
  reset: PaginationResetCallback = dummyCallback,
) => checkboxFilter({
  value,
  name: 'priority',
  label: t('form.only-high-prio'),
  reset,
});

export const todoOverdueFilter = (
  value: Ref<boolean>,
  reset: PaginationResetCallback = dummyCallback,
) => checkboxFilter({
  value,
  name: 'overdue',
  label: t('form.only-overdue'),
  reset,
});

export const notificationUnreadFilter = (
  value: Ref<boolean>,
  reset: PaginationResetCallback = dummyCallback,
) => checkboxFilter({
  value,
  name: 'unreadOnly',
  label: t('form.only-unread'),
  reset,
});

export const templateCategoryFilter = (
  value: Ref<string | null>,
  reset: PaginationResetCallback = dummyCallback,
  placeholder = t('common.industry'),
) => dropdownFilter<string | null>({
  value,
  name: 'industry',
  label: t('common.industry'),
  placeholder,
  options: [{
    value: null,
    label: t('form.all-industries'),
  }, ...templateCategory.value.map(c => ({ label: c.title, value: c.slug }))],
  reset,
  hideLabel: true,
});
